import React from 'react';
import { Download, Mail, ShieldCheck, Smartphone, UserX, AlertTriangle, CheckCircle2, Info } from 'lucide-react';

// ITOnboardingGuide
// ─────────────────────────────────────────────────────────────────────
// In-app rendering of the IT_ONBOARDING_GUIDE.docx content. Designed
// to be embedded in the Help Center via the `Component` field on the
// HELP_ARTICLES entry. Mirrors the DOCX sections so an IT person can
// read it without leaving the browser, or download the Word version
// for offline reference / printing.
//
// Content stays in sync with `scripts/build_it_onboarding_docx.py` —
// the source of truth is the DOCX (regenerated by that script). When
// you update one, update the other.

// Small visual primitives so the section bodies stay declarative. Each
// callout maps to the colored boxes in the DOCX.

const Callout = ({ tone = 'info', title, children }) => {
  const toneClasses = {
    info:    'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-200 dark:border-indigo-800 text-indigo-900 dark:text-indigo-200',
    tip:     'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800 text-emerald-900 dark:text-emerald-200',
    warn:    'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 text-amber-900 dark:text-amber-200',
    danger:  'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-900 dark:text-red-200',
  }[tone];
  const Icon = { info: Info, tip: CheckCircle2, warn: AlertTriangle, danger: ShieldCheck }[tone];
  return (
    <div className={`rounded-xl border px-4 py-3 my-3 text-sm ${toneClasses}`}>
      <div className="flex items-start gap-2.5">
        <Icon size={16} className="mt-0.5 flex-shrink-0" />
        <div>
          <p className="font-semibold mb-0.5">{title}</p>
          <p>{children}</p>
        </div>
      </div>
    </div>
  );
};

const SectionH = ({ children, icon: Icon }) => (
  <h3 className="text-base font-bold text-slate-900 dark:text-white mt-6 mb-3 flex items-center gap-2 border-b border-slate-200 dark:border-slate-800 pb-2">
    {Icon && <Icon size={18} className="text-indigo-600 dark:text-indigo-400" />}
    {children}
  </h3>
);

const SubH = ({ children }) => (
  <h4 className="text-sm font-semibold text-slate-900 dark:text-white mt-4 mb-2">{children}</h4>
);

// Numbered list — explicit indices kept so the visual matches the DOCX
// step-by-step style. Body item is a JSX fragment so we can mix bold
// runs and inline emphasis.
const Steps = ({ items }) => (
  <ol className="space-y-2 my-3">
    {items.map((item, i) => (
      <li key={i} className="flex gap-3 text-sm text-slate-700 dark:text-slate-200">
        <span className="text-indigo-600 dark:text-indigo-400 font-semibold flex-shrink-0">{i + 1}.</span>
        <span>{item}</span>
      </li>
    ))}
  </ol>
);

// Bulleted list — used for "what they get" / "who's it for" content.
const Bullets = ({ items }) => (
  <ul className="space-y-1.5 my-3 ml-4 list-disc text-sm text-slate-700 dark:text-slate-200">
    {items.map((item, i) => <li key={i}>{item}</li>)}
  </ul>
);

// Role × confidentiality table (matches the DOCX layout).
const ROLES_TABLE = [
  { role: 'Admin',     who: 'Senior partner / office manager who runs everything.',                            sees: 'Yes — all matters firm-wide.' },
  { role: 'Partner',   who: 'Equity / senior partners. Same access as Admin minus role management.',           sees: 'Yes — all matters firm-wide.' },
  { role: 'Attorney',  who: 'Practising attorneys (junior or senior).',                                        sees: 'Only when assigned to the matter.' },
  { role: 'Associate', who: 'Junior attorneys still under supervision.',                                       sees: 'Only when assigned to the matter.' },
  { role: 'Paralegal', who: 'Paralegals, legal assistants, case clerks.',                                      sees: 'Only when assigned to the matter.' },
  { role: 'Clerk',     who: 'Filing clerks, office assistants, secretaries.',                                  sees: 'Only when assigned to the matter.' },
  { role: 'IT',        who: 'You. Settings + team management + documents. No case / billing access.',         sees: 'No — IT cannot see matters at all.' },
];

// Page-by-page access table (Confirm Their Profile section).
const ACCESS_TABLE = [
  { page: 'Dashboard', sees: 'Yes — everyone sees the firm dashboard.' },
  { page: 'Matters',   sees: 'Yes — public matters. Confidential matters only if assigned.' },
  { page: 'Calendar',  sees: 'Yes — own events + matters they\'re assigned to.' },
  { page: 'Tasks',     sees: 'Yes — their assigned tasks.' },
  { page: 'Documents', sees: 'Yes — for matters they can access.' },
  { page: 'Billing',   sees: 'Admin / Partner / Accountant only. Others see nothing here.' },
  { page: 'Reports',   sees: 'Admin / Partner only.' },
  { page: 'Settings',  sees: 'Admin / Partner only.' },
];

const TROUBLESHOOTING = [
  { symptom: 'New employee never got the invite email.', fix: 'Check their spam folder. Then Settings → Team → Resend invite.' },
  { symptom: '"Verifying…" forever on the invite link.', fix: 'Link expired or opened in a different browser than the email. Resend; open it in the same browser.' },
  { symptom: '"No other team members found" in Direct Messages.', fix: 'Sign out and sign back in. Reach out to support if it persists.' },
  { symptom: '"New row violates row-level security policy" on any action.', fix: 'Sign out fully (not just refresh) and back in. Report to support with a screenshot if it persists.' },
  { symptom: 'Employee can\'t see a matter they should see.', fix: 'Check Matter → Team. The responsible attorney needs to add them. Admins / Partners see everything automatically.' },
  { symptom: 'Employee can\'t change their password.', fix: 'Profile → Change password. Admin can also trigger a reset from Settings → Team → their row.' },
  { symptom: 'Mobile site looks broken / cards too big.', fix: 'Hard-refresh (close all tabs, reopen). Most fixes ship every Friday; cached old versions are the usual culprit.' },
];

export default function ITOnboardingGuide() {
  return (
    <div className="space-y-2">
      {/* Hero with download button */}
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/60 px-4 py-3 flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-900 dark:text-white">
            For IT / Office Manager / Firm Administrator
          </p>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            Onboard every new employee — attorneys, paralegals, clerks, accountants, and admin staff. ~10–15 minutes per person.
          </p>
        </div>
        <a
          href={`${process.env.PUBLIC_URL || ''}/IT_ONBOARDING_GUIDE.docx`}
          download="IT_ONBOARDING_GUIDE.docx"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium transition flex-shrink-0"
        >
          <Download size={14} /> Download Word version
        </a>
      </div>

      {/* §1 Before you start */}
      <SectionH icon={Info}>1. Before you start</SectionH>
      <p className="text-sm text-slate-700 dark:text-slate-200">
        Have these three things ready before you click anything in DocketFlow:
      </p>
      <Bullets items={[
        'The employee\'s full legal name (as it appears on their ID).',
        'Their personal or work email address — they must be able to access it from a phone or laptop to confirm the invite link.',
        'Their Ghanaian mobile number in 0XXXXXXXXX format (10 digits, starting with 0). DocketFlow normalises this to +233 automatically.',
      ]} />
      <Callout tone="tip" title="TIP">
        If you don’t yet know the employee’s role on their first day, invite them as a Clerk. You can promote them later from Settings → Team without re-sending the invite.
      </Callout>

      {/* §2 Pick the right role */}
      <SectionH icon={ShieldCheck}>2. Pick the right role</SectionH>
      <p className="text-sm text-slate-700 dark:text-slate-200">
        Every employee fits into one of these roles. The role controls what they can see and edit across matters, billing, settings, and confidential cases.
      </p>
      <div className="my-3 overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800">
        <table className="min-w-full text-xs">
          <thead className="bg-indigo-600 text-white">
            <tr>
              <th className="px-3 py-2 text-left font-semibold">Role</th>
              <th className="px-3 py-2 text-left font-semibold">Who it's for</th>
              <th className="px-3 py-2 text-left font-semibold">Sees confidential matters?</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-800 bg-white dark:bg-slate-900">
            {ROLES_TABLE.map((r, i) => (
              <tr key={r.role} className={i % 2 === 1 ? 'bg-slate-50 dark:bg-slate-800/40' : ''}>
                <td className="px-3 py-2 font-semibold text-slate-900 dark:text-white">{r.role}</td>
                <td className="px-3 py-2 text-slate-700 dark:text-slate-300">{r.who}</td>
                <td className="px-3 py-2 text-slate-700 dark:text-slate-300">{r.sees}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Callout tone="danger" title="IMPORTANT">
        Only Admin and Partner roles see confidential matters automatically. For every other role, the responsible attorney must explicitly add them to the matter team before they can see it.
      </Callout>
      <Callout tone="info" title="ABOUT THE IT ROLE">
        Use this role for yourself if you’re the firm’s IT person but not a lawyer or partner. It gives you everything you need to onboard staff and configure settings — but you will NOT see client matters, billing, or contact lists. That’s intentional: it protects client confidentiality even from the person setting up their accounts.
      </Callout>

      {/* §3 Send the invite */}
      <SectionH icon={Mail}>3. Send the invite</SectionH>
      <Steps items={[
        'Sign into DocketFlow as an Admin or Partner.',
        'Go to Settings (gear icon, bottom-left) → Team tab.',
        'Click Invite Team Member.',
        'Enter first name, last name, email, mobile, and role (from §2).',
        'Click Send invite.',
      ]} />
      <p className="text-sm text-slate-700 dark:text-slate-200 italic">
        You’ll see the new employee appear in the team list with a yellow <strong>Pending</strong> status. They stay Pending until they confirm their email.
      </p>
      <Callout tone="warn" title="NOTE">
        The invite link expires 7 days after it’s sent. If the employee doesn’t act on it in time, click Resend invite on their row — this generates a fresh link without re-creating the user.
      </Callout>

      {/* §4 First login */}
      <SectionH>4. Walk the employee through their first login</SectionH>
      <p className="text-sm text-slate-700 dark:text-slate-200">
        Send this exact sequence to the employee, either in person or as a text / email:
      </p>
      <Steps items={[
        'Open the invite email from DocketFlow.',
        'Click the Accept invitation link.',
        'A page asks them to set a password — 8+ characters, must include a number.',
        'After setting the password they’re signed in automatically.',
        'They’ll be prompted to fill in their employee profile (date of birth, address, qualifications).',
        'Save. They’re now active.',
      ]} />
      <Callout tone="tip" title="TIP">
        Tell employees to use a personal device for the first login if they’re remote — the invite link contains a one-time token that only works in the browser that received it. Don’t forward the email between devices.
      </Callout>

      {/* §5 Confirm */}
      <SectionH>5. Confirm their profile and access</SectionH>
      <p className="text-sm text-slate-700 dark:text-slate-200">
        Once the employee says they’re in, sign in as an Admin and verify:
      </p>
      <Bullets items={[
        'Settings → Team: their row no longer says Pending — it says Active.',
        'Their role on the row matches §2.',
        'Click their name → confirm the profile fields are filled (DOB, address, mobile).',
        'Their mobile number shows in +233 format — if it doesn’t, ask them to edit and re-save it.',
      ]} />
      <SubH>Page-by-page access spot check:</SubH>
      <div className="my-3 overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800">
        <table className="min-w-full text-xs">
          <thead className="bg-indigo-600 text-white">
            <tr>
              <th className="px-3 py-2 text-left font-semibold">Page</th>
              <th className="px-3 py-2 text-left font-semibold">Should they see content?</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-800 bg-white dark:bg-slate-900">
            {ACCESS_TABLE.map((r, i) => (
              <tr key={r.page} className={i % 2 === 1 ? 'bg-slate-50 dark:bg-slate-800/40' : ''}>
                <td className="px-3 py-2 font-semibold text-slate-900 dark:text-white">{r.page}</td>
                <td className="px-3 py-2 text-slate-700 dark:text-slate-300">{r.sees}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* §6 Role-specific */}
      <SectionH>6. Role-specific configuration</SectionH>
      <SubH>Attorneys / Associates</SubH>
      <Bullets items={[
        'Add them to Matter → Team for every matter they should work on.',
        'If they’ll be the lead attorney, set them as the matter’s responsible attorney (Edit matter → Responsible attorney).',
        'If they’ll bill time, set their hourly rate on their employee profile.',
      ]} />
      <SubH>Paralegals / Clerks</SubH>
      <Bullets items={[
        'Same matter-assignment rules as attorneys — they only see matters they’re explicitly on.',
        'They cannot create invoices or trust transactions — that’s deliberate.',
        'They can create matters and contacts, log time, manage tasks, upload documents.',
      ]} />
      <SubH>Accountants</SubH>
      <Bullets items={[
        'Grant read access to Billing → Trust accounts and Reports.',
        'They can record trust transactions and reconcile but cannot delete payments — that’s an Admin-only action.',
      ]} />
      <SubH>Administrative staff (Office Manager, Receptionist, Secretaries)</SubH>
      <Bullets items={[
        'Use Clerk role unless they need broader access.',
        'Add them to matter teams for the matters they support — otherwise they can’t open the client folder.',
        'If they handle intake, add them to all open matters by default.',
      ]} />

      {/* §7 Security */}
      <SectionH icon={ShieldCheck}>7. Security baseline — for every employee</SectionH>
      <Steps items={[
        'Strong password (at least 12 characters; mix of letters, numbers, symbols).',
        'Sign out at the end of every day — especially on shared computers.',
        'Never share login credentials. If a colleague needs access, invite them as their own user.',
        'Confidential matters: if you don’t see the matter, you weren’t meant to. Don’t ask a colleague to forward documents or screenshots.',
        'Phishing: DocketFlow will never email asking for your password. Forward suspicious emails to IT.',
      ]} />
      <Callout tone="danger" title="WARNING">
        End-to-end encryption: if your firm uses encrypted channels in Team Chat, the encryption passphrase is firm-wide and must be set by an Admin or Partner. Give the passphrase to new employees through a secure channel (in person, signed encrypted message — never email or SMS).
      </Callout>

      {/* §8 Mobile */}
      <SectionH icon={Smartphone}>8. Mobile setup (optional but recommended)</SectionH>
      <p className="text-sm text-slate-700 dark:text-slate-200">
        DocketFlow runs in the phone browser. There’s no separate app to install. To get an app-like experience:
      </p>
      <SubH>iPhone / iPad</SubH>
      <Steps items={[
        'Open DocketFlow in Safari (must be Safari — not Chrome).',
        'Tap the Share button (square with arrow).',
        'Scroll and tap Add to Home Screen.',
        'Tap Add. A DocketFlow icon appears on the home screen.',
      ]} />
      <SubH>Android</SubH>
      <Steps items={[
        'Open DocketFlow in Chrome.',
        'Tap the three-dot menu (top-right).',
        'Tap Install app or Add to Home screen.',
        'Confirm. The icon installs to the home screen and app drawer.',
      ]} />
      <Callout tone="tip" title="TIP">
        Once installed to the home screen, push notifications for @-mentions, matter updates, and deadline reminders can be enabled in Settings → Notifications.
      </Callout>

      {/* §9 Offboarding */}
      <SectionH icon={UserX}>9. When an employee leaves — offboarding</SectionH>
      <SubH>Day of departure</SubH>
      <Steps items={[
        'Settings → Team → click their row → Suspend account. They lose access immediately.',
        'Reassign their open matters: Matter → Edit → change Responsible attorney.',
        'Reassign open tasks: Tasks page → filter by assignee → bulk reassign.',
        'If they were on confidential matter teams, remove them from Matter → Team.',
      ]} />
      <SubH>Within 30 days</SubH>
      <Steps items={[
        'Settings → Team → suspended row → Delete account. This is a soft delete — their work product stays, their login is removed permanently.',
        'Audit Reports → Activity log for the last 90 days of their activity. Save a copy if required by firm policy.',
      ]} />
      <Callout tone="danger" title="WARNING">
        Do NOT hard-delete a departing employee on the same day. Wait at least 7 days in case they need to be queried for handover. Suspension achieves the access-revocation goal without losing any data.
      </Callout>

      {/* §10 Troubleshooting */}
      <SectionH icon={AlertTriangle}>10. Troubleshooting cheat sheet</SectionH>
      <div className="my-3 overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800">
        <table className="min-w-full text-xs">
          <thead className="bg-indigo-600 text-white">
            <tr>
              <th className="px-3 py-2 text-left font-semibold">Symptom</th>
              <th className="px-3 py-2 text-left font-semibold">First thing to try</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-800 bg-white dark:bg-slate-900">
            {TROUBLESHOOTING.map((row, i) => (
              <tr key={i} className={i % 2 === 1 ? 'bg-slate-50 dark:bg-slate-800/40' : ''}>
                <td className="px-3 py-2 font-semibold text-slate-900 dark:text-white">{row.symptom}</td>
                <td className="px-3 py-2 text-slate-700 dark:text-slate-300">{row.fix}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Quick reference */}
      <SectionH icon={CheckCircle2}>Quick reference card</SectionH>
      <p className="text-sm text-slate-500 dark:text-slate-400 italic mb-2">Print this and stick it next to the IT desk.</p>
      <SubH>New hire — 5-minute path:</SubH>
      <Steps items={[
        'Settings → Team → Invite Team Member.',
        'Fill name + email + mobile + role.',
        'Send.',
        'Tell employee to check email (incl. spam).',
        'After they sign in, verify Active status.',
      ]} />
      <SubH>Departure — same-day path:</SubH>
      <Steps items={[
        'Settings → Team → their row → Suspend.',
        'Reassign open matters (Edit matter → Responsible attorney).',
        'Reassign open tasks (Tasks → bulk reassign).',
        'Delete after 30 days.',
      ]} />
    </div>
  );
}
